import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import { useAppSelector } from 'src/hooks/hooks';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { CircularProgress } from '@mui/material';

// Components
import FormHeader from './components/FormHeader';
import BasicInformation from './components/BasicInformation';
import ContactDetails from './components/ContactDetails';
import EmploymentDetails from './components/EmploymentDetails';
import {
  addEmployee,
  addManagers,
  getNextEmployeeId
} from 'src/services/employeeService';

// Services
import {
  createCandidate,
  createDocumentAcknowledgement,
  updateCandidate,
  updateEmsUserCreated
} from 'src/services/onboardingService';
import { getAllRoles } from 'src/services/RoleManagementService';

// Types
import { CandidateFormProps } from './types/candidate.types';
import { normalizeEmploymentType } from './constants/formOptions';

// Hooks
import { useCandidateValidation } from './hooks/useCandidateValidation';

// Styles
import {
  dialogStyles,
  dialogContentStyles,
  dialogActionsStyles,
  buttonStyles
} from './styles/CandidateForm.styles';

const CandidateForm: React.FC<CandidateFormProps> = ({
  open,
  onClose,
  mode,
  candidate,
  onSuccess,
  onError
}) => {
  const { register, handleSubmit, reset } = useForm();
  const dispatch = useDispatch();

  // State management
  const [firstPageValidation, setFirstPageValidation] = useState<any>({});
  const [firstErrors, setFirstErrors] = useState<any>({});
  const [secondPageValidation, setSecondPageValidation] = useState<any>({});
  const [secondErrors, setSecondErrors] = useState<any>({});
  const [thirdPageValidation, setThirdPageValidation] = useState<any>({});
  const [thirdErrors, setThirdErrors] = useState<any>({});

  const [selectedGender, setSelectedGender] = useState('');
  const [selectedMaritalStatus, setSelectedMaritalStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState<any>(null);
  const [selectedManager, setSelectedManager] = useState<any>(null);
  const [selectedUserRole, setSelectedUserRole] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const isCreateEmsUserMode = mode === 'createEmsUser';

  // Get loading state from Redux for consistency
  const { isLoading: globalLoading } = useAppSelector(
    (state) => state.appReducer
  );

  // Custom validation hook
  const { validateAndSetErrors, clearError } = useCandidateValidation({
    firstPageValidation,
    secondPageValidation,
    thirdPageValidation,
    selectedDesignation,
    selectedManager,
    selectedUserRole,
    selectedGender,
    selectedMaritalStatus,
    selectedType,
    setFirstErrors,
    setSecondErrors,
    setThirdErrors,
    isCreateEmsUserMode
  });

  // Helper function to show snackbar messages
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setSnackBarMessage(message);
    setSnackbarSeverity(severity);
    setSnackBarOpen(true);
  };

  // Improved phone number formatter that ensures + format
  const formatPhoneNumber = (number: string) => {
    if (!number) return '';
    
    // Ensure the number always has + format
    if (number.startsWith('+')) {
      return number;
    } else if (number.startsWith('00')) {
      // Convert 00 format to +
      return '+' + number.substring(2);
    } else {
      // Add + if missing
      return '+' + number;
    }
  };

  // Function to map candidate roles to the correct format
  const mapCandidateRoles = async (roleString: string): Promise<any[]> => {
    if (!roleString) return [];
    
    try {
      const allRoles = await getAllRoles();
      const roleLabels = roleString.split(',').map((r) => r.trim());
      
      const mappedRoles = roleLabels.map((label) => {
        const foundRole = allRoles.data?.find((r: any) => 
          r.name === label || r.name?.toLowerCase() === label.toLowerCase()
        );
        return foundRole ? {
          value: foundRole.roleId || foundRole.value || label,
          label: foundRole.name || foundRole.label || label,
          original: foundRole
        } : {
          value: label,
          label: label
        };
      });
      
      return mappedRoles;
    } catch (error) {
      // Fallback: create basic role objects
      const roleLabels = roleString.split(',').map((r) => r.trim());
      return roleLabels.map((label) => ({
        value: label,
        label: label
      }));
    }
  };

  // Auto-fill department and tier based on designation
  useEffect(() => {
    if (selectedDesignation) {
      setThirdPageValidation((prev: any) => ({
        ...prev,
        department: selectedDesignation.department || '',
        tier: selectedDesignation.tier || ''
      }));
    }
  }, [selectedDesignation]);

  useEffect(() => {
    if (open && isCreateMode) {
      getNextEmployeeId()
        .then((res) => {
          // Extract employeeId from res.data
          const nextId =
            typeof res.data === 'string' || typeof res.data === 'number'
              ? String(res.data)
              : '';
          setThirdPageValidation((prev: any) => ({
            ...prev,
            employeeId: nextId
          }));
        })
        .catch(() => {
          setThirdPageValidation((prev: any) => ({
            ...prev,
            employeeId: ''
          }));
        });
    }
  }, [open, isCreateMode]);

  useEffect(() => {
    if (
      mode === 'create' &&
      firstPageValidation.firstName &&
      firstPageValidation.lastName
    ) {
      // Remove spaces and special characters, convert to lowercase
      const cleanFirstName = firstPageValidation.firstName
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9]/g, '');
      const cleanLastName = firstPageValidation.lastName
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9]/g, '');

      const officialEmail = `${cleanFirstName}.${cleanLastName}@digiratina.com`;
      setFirstPageValidation((prev: any) => ({
        ...prev,
        officialEmail: officialEmail
      }));
    }
  }, [firstPageValidation.firstName, firstPageValidation.lastName, mode]);

  useEffect(() => {
    // Only run when dialog is open AND we have candidate data for view/edit modes
    if (
      open &&
      candidate &&
      (mode === 'view' || mode === 'edit' || mode === 'createEmsUser')
    ) {
      const populateFormData = async () => {
        // Parse the name
        const nameParts = candidate.name?.split(' ') || [];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Set basic information
        setFirstPageValidation({
          firstName: firstName,
          lastName: lastName,
          nic: candidate.nic || '',
          dateOfBirth: candidate.dob || '',
          personalEmail: candidate.email || '',
          officialEmail: candidate.officialEmail || ''
        });

        // Set contact details with properly formatted phone numbers
        setSecondPageValidation({
          mobileNumber: formatPhoneNumber(candidate.phoneNumber),
          permanentAddress: candidate.address || '',
          currentAddress: candidate.currentAddress || '',
          emergencyContactName:
            candidate.emergencyContactDetails?.[0]?.name || '',
          emergencyContactRelationship:
            candidate.emergencyContactDetails?.[0]?.relationship || '',
          emergencyContactNumber: formatPhoneNumber(
            candidate.emergencyContactDetails?.[0]?.number || ''
          )
        });

        // Set employment details
        setThirdPageValidation({
          employeeId: candidate.employeeId || '',
          joinDate: candidate.joinedDate || '',
          fromDate:
            candidate.employeeTypeStartingDate || candidate.joinedDate || '',
          department: candidate.departmentCode || '',
          tier: candidate.tier || ''
        });

        setSelectedGender(
          candidate.genderCode === 'Male'
            ? 'male'
            : candidate.genderCode === 'Female'
            ? 'female'
            : ''
        );

        // Set marital status
        setSelectedMaritalStatus(
          candidate.maritalStatusCode === 'Married'
            ? 'married'
            : candidate.maritalStatusCode === 'Single'
            ? 'single'
            : ''
        );

        // Set employee type
        setSelectedType(normalizeEmploymentType(candidate.employeeType));

        // Set designation with proper null check
        if (candidate.designationCode) {
          setSelectedDesignation({
            value: candidate.designationCode,
            label: candidate.designationCode,
            department: candidate.departmentCode,
            tier: candidate.tier
          });
        } else {
          setSelectedDesignation(null);
        }

        // Set manager with proper null check
        if (candidate.primaryManager) {
          setSelectedManager({
            value: candidate.primaryManager,
            label: candidate.primaryManager
          });
        } else {
          setSelectedManager(null);
        }

        // Set user roles - FIXED: Use the mapping function
        if (candidate.role) {
          try {
            const mappedRoles = await mapCandidateRoles(candidate.role);
            setSelectedUserRole(mappedRoles);
          } catch (error) {
            // Fallback to basic mapping
            const roleLabels = candidate.role.split(',').map((r) => r.trim());
            const roleObjects = roleLabels.map((label) => ({
              value: label,
              label: label
            }));
            setSelectedUserRole(roleObjects);
          }
        } else {
          setSelectedUserRole([]);
        }
      };

      // Add a small delay to ensure all components are mounted
      const timer = setTimeout(() => {
        populateFormData();
      }, 50);

      // Cleanup function to prevent memory leaks
      return () => clearTimeout(timer);
    }
  }, [candidate, mode, open]);

  useEffect(() => {
    if (!open || mode === 'create' || mode === 'createEmsUser') {
      // Reset all form states
      setFirstPageValidation({});
      setSecondPageValidation({});
      setThirdPageValidation({});
      setSelectedGender('');
      setSelectedMaritalStatus('');
      setSelectedType('');
      setSelectedDesignation(null);
      setSelectedManager(null);
      setSelectedUserRole([]);
      setFirstErrors({});
      setSecondErrors({});
      setThirdErrors({});
      reset();
    }
  }, [open, mode, reset]);

  useEffect(() => {
    if (open) {
      setSnackBarOpen(false);
      setSnackBarMessage('');
      setSnackbarSeverity('');
    }
  }, [open]);

  const resetFormAndClose = () => {
    // Reset all form states and errors when closing
    setFirstPageValidation({});
    setSecondPageValidation({});
    setThirdPageValidation({});
    setSelectedGender('');
    setSelectedMaritalStatus('');
    setSelectedType('');
    setSelectedDesignation(null);
    setSelectedManager(null);
    setSelectedUserRole([]);
    setFirstErrors({});
    setSecondErrors({});
    setThirdErrors({});
    setSnackBarOpen(false);
    setSnackBarMessage('');
    setSnackbarSeverity('');
    setShowDiscardDialog(false);
    reset();
    onClose();
  };

  const handleClose = (event?: React.MouseEvent<HTMLButtonElement>): void => {
    if (isLoading || globalLoading) {
      showSnackbar(
        'Please wait for the current operation to complete.',
        'warning'
      );
      return;
    }

    // If in view mode, close directly without confirmation
    if (isViewMode) {
      resetFormAndClose();
      return;
    }

    // For create/edit/createEmsUser modes, show confirmation dialog
    setShowDiscardDialog(true);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
  };

  const handleDiscardConfirm = () => {
    resetFormAndClose();
  };

  const getDiscardMessage = () => {
    if (isCreateMode) {
      return 'Are you sure you want to discard the new candidate information?';
    } else if (isEditMode) {
      return 'Are you sure you want to discard the changes made to this candidate?';
    } else if (isCreateEmsUserMode) {
      return 'Are you sure you want to discard the EMS user creation process?';
    }
    return 'Are you sure you want to discard changes?';
  };

  const onSubmit = async (data: any, event?: React.BaseSyntheticEvent) => {
    if (isLoading || globalLoading) {
      return;
    }

    // Clear any previous snackbar message before starting a new submit flow.
    setSnackBarOpen(false);
    setSnackBarMessage('');
    setSnackbarSeverity('');

    setIsLoading(true);

    dispatch(AppActions.showLoader());

    // Let React paint loading state before running validation and API calls.
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });

    // Validate before submit
    if (!validateAndSetErrors()) {
      setIsLoading(false);
      dispatch(AppActions.hideLoader());
      return;
    }

    // Build candidate payload - preserve + format in phone numbers
    const candidatePayload = {
      candidate: {
        officialEmail:
          firstPageValidation.officialEmail ||
          `${firstPageValidation.firstName?.toLowerCase()}.${firstPageValidation.lastName?.toLowerCase()}@digiratina.com`,
        nic: firstPageValidation.nic?.trim() || '',
        name: `${firstPageValidation.firstName?.trim() || ''} ${
          firstPageValidation.lastName?.trim() || ''
        }`.trim(),
        designationCode: selectedDesignation?.label || '',
        departmentCode:
          selectedDesignation?.department ||
          thirdPageValidation.department ||
          '',
        joinedDate: thirdPageValidation.joinDate || '',
        employeeId: thirdPageValidation.employeeId?.trim() || '',
        genderCode:
          selectedGender === 'male'
            ? 'Male'
            : selectedGender === 'female'
            ? 'Female'
            : '',
        maritalStatusCode:
          selectedMaritalStatus === 'married'
            ? 'Married'
            : selectedMaritalStatus === 'single'
            ? 'Single'
            : '',
        skypeId: '',
        address: secondPageValidation.permanentAddress?.trim() || '',
        currentAddress: secondPageValidation.currentAddress?.trim() || '',
        phoneNumber: secondPageValidation.mobileNumber || '', // Keep with + format
        employeeType:
          selectedType === 'intern'
            ? 'INTERN'
            : selectedType === 'probation'
            ? 'PROBATION'
            : '',
        dob: firstPageValidation.dateOfBirth
          ? new Date(firstPageValidation.dateOfBirth).toISOString()
          : null,
        tier: selectedDesignation?.tier || thirdPageValidation.tier || '',
        primaryManager: selectedManager?.value || '',
        employeeTypeStartingDate: thirdPageValidation.fromDate || '',
        role:
          Array.isArray(selectedUserRole) && selectedUserRole.length > 0
            ? selectedUserRole.map((role) => role.label).join(',')
            : 'General User',

        createJiraAccount: true,
        jiraLead: false,
        employeeTypeEndingDate: null,
        carrierStartDate: '',
        email: firstPageValidation.personalEmail?.trim() || ''
      },
      emergencyContacts: []
    };

    // Add emergency contact if provided - preserve + format
    if (
      secondPageValidation.emergencyContactName?.trim() &&
      secondPageValidation.emergencyContactNumber?.trim()
    ) {
      candidatePayload.emergencyContacts.push({
        name: secondPageValidation.emergencyContactName.trim(),
        relationship:
          secondPageValidation.emergencyContactRelationship?.trim() ||
          'Emergency Contact',
        number: secondPageValidation.emergencyContactNumber || '', // Keep with + format
        createdDate: new Date().toISOString().split('T')[0]
      });
    }

    // Handle different modes
    if (isCreateMode) {
      createCandidate(candidatePayload)
        .then(() => {
          setIsLoading(false);
          dispatch(AppActions.hideLoader());

          // Show success message
          const successMsg = `Candidate "${firstPageValidation.firstName} ${firstPageValidation.lastName}" has been added successfully!`;

          if (onSuccess) onSuccess(successMsg);
          resetFormAndClose();
        })
        .catch((error) => {
          setIsLoading(false);
          dispatch(AppActions.hideLoader());

          // Determine error message based on error type
          let errorMsg = 'Unable to add the candidate. Please try again.';

          if (error?.response?.status === 400) {
            errorMsg =
              'Invalid data provided. Please check your inputs and try again.';
          } else if (error?.response?.status === 409) {
            errorMsg =
              'A candidate with this email or NIC already exists in the system.';
          } else if (error?.response?.status === 422) {
            errorMsg =
              'Some required fields are missing or invalid. Please review your input.';
          } else if (error?.response?.status === 500) {
            errorMsg =
              'Server error occurred. Please contact system administrator.';
          } else if (error?.response?.data?.message) {
            errorMsg = `${error.response.data.message}`;
          } else if (error?.message) {
            errorMsg = `${error.message}`;
          }

          showSnackbar(errorMsg, 'error');

          if (onError) onError(error.message || 'Failed to create candidate');
        });
    } else if (isEditMode) {
      // Handle update mode
      if (!candidate?.id) {
        setIsLoading(false);
        dispatch(AppActions.hideLoader());
        showSnackbar('Candidate ID is missing. Cannot update.', 'error');
        return;
      }

      // Build update payload according to API structure
      const updatePayload = {
        id: candidate.id,
        name: `${firstPageValidation.firstName?.trim() || ''} ${
          firstPageValidation.lastName?.trim() || ''
        }`.trim(),
        startDate: thirdPageValidation.joinDate || null,
        status: candidate.status || '',
        nic: firstPageValidation.nic?.trim() || '',
        dob: firstPageValidation.dateOfBirth || null,
        genderCode:
          selectedGender === 'male'
            ? 'Male'
            : selectedGender === 'female'
            ? 'Female'
            : '',
        maritalStatusCode:
          selectedMaritalStatus === 'married'
            ? 'Married'
            : selectedMaritalStatus === 'single'
            ? 'Single'
            : '',
        email: firstPageValidation.personalEmail?.trim() || '',
        officialEmail: firstPageValidation.officialEmail?.trim() || '',
        phoneNumber: secondPageValidation.mobileNumber || '', // Keep with + format
        address: secondPageValidation.permanentAddress?.trim() || '',
        currentAddress: secondPageValidation.currentAddress?.trim() || '',
        emergencyContactDetails: [],
        employeeType:
          selectedType === 'intern'
            ? 'INTERN'
            : selectedType === 'probation'
            ? 'PROBATION'
            : 'FULLTIME',
        designationCode: selectedDesignation?.value || '',
        departmentCode:
          selectedDesignation?.department ||
          thirdPageValidation.department ||
          '',
        tier: selectedDesignation?.tier || thirdPageValidation.tier || '',
        primaryManager: selectedManager?.value || '',
        joinedDate: thirdPageValidation.joinDate || '',
        employeeTypeStartingDate:
          thirdPageValidation.fromDate || thirdPageValidation.joinDate || '',
        role:
          Array.isArray(selectedUserRole) && selectedUserRole.length > 0
            ? selectedUserRole.map((role) => role.label).join(',')
            : 'General User',
        createJiraAccount: true,
        jiraLead: false,
        employeeTypeEndingDate: null,
        carrierStartDate: thirdPageValidation.joinDate || ''
      };

      // Add emergency contact if provided - preserve + format
      if (
        secondPageValidation.emergencyContactName?.trim() &&
        secondPageValidation.emergencyContactNumber?.trim()
      ) {
        updatePayload.emergencyContactDetails.push({
          name: secondPageValidation.emergencyContactName.trim(),
          relationship:
            secondPageValidation.emergencyContactRelationship?.trim() ||
            'Emergency Contact',
          number: secondPageValidation.emergencyContactNumber || '' // Keep with + format
        });
      }

      updateCandidate(candidate.id, updatePayload)
        .then(() => {
          setIsLoading(false);
          dispatch(AppActions.hideLoader());

          // Compare original and updated values
          const originalFirstName = candidate.name?.split(' ')[0] || '';
          const originalLastName = candidate.name?.split(' ').slice(1).join(' ') || '';
          const originalDesignation = candidate.designationCode || '';
          const originalMobile = candidate.phoneNumber || '';
          const originalJoinDate = candidate.joinedDate || '';

          const updatedFirstName = firstPageValidation.firstName?.trim() || '';
          const updatedLastName = firstPageValidation.lastName?.trim() || '';
          const updatedDesignation = selectedDesignation?.value || '';
          const updatedMobile = secondPageValidation.mobileNumber || '';
          const updatedJoinDate = thirdPageValidation.joinDate || '';

          const isSpecialUpdate =
            originalFirstName !== updatedFirstName ||
            originalLastName !== updatedLastName ||
            originalDesignation !== updatedDesignation ||
            originalMobile !== updatedMobile ||
            originalJoinDate !== updatedJoinDate;

          console.log('Is special update:', isSpecialUpdate);
          
          const successMsg = isSpecialUpdate
            ? 'Candidate updated successfully. Notification email sent.'
            : `Candidate "${updatedFirstName} ${updatedLastName}" has been updated successfully!`;

          if (onSuccess) onSuccess(successMsg);
          resetFormAndClose();
        })
        .catch((error) => {
          setIsLoading(false);
          dispatch(AppActions.hideLoader());

          let errorMsg = 'Unable to update the candidate. Please try again.';

          if (error?.response?.status === 400) {
            errorMsg =
              'Invalid data provided. Please check your inputs and try again.';
          } else if (error?.response?.status === 404) {
            errorMsg = 'Candidate not found. It may have been deleted.';
          } else if (error?.response?.status === 409) {
            errorMsg =
              'A candidate with this email or NIC already exists in the system.';
          } else if (error?.response?.status === 422) {
            errorMsg =
              'Some required fields are missing or invalid. Please review your input.';
          } else if (error?.response?.status === 500) {
            errorMsg =
              'Server error occurred. Please contact system administrator.';
          } else if (error?.response?.data?.message) {
            errorMsg = `${error.response.data.message}`;
          } else if (error?.message) {
            errorMsg = `${error.message}`;
          }

          showSnackbar(errorMsg, 'error');
          if (onError) onError(error.message || 'Failed to update candidate');
        });
    } else if (isCreateEmsUserMode) {
      // Call addEmployee first, then updateEmsUserCreated if successful
      if (!candidate?.id) {
        setIsLoading(false);
        dispatch(AppActions.hideLoader());
        showSnackbar(
          'Candidate ID is missing. Cannot create EMS user.',
          'error'
        );
        return;
      }

      const updatePayload = {
        id: candidate.id,
        name: `${firstPageValidation.firstName?.trim() || ''} ${
          firstPageValidation.lastName?.trim() || ''
        }`.trim(),
        startDate: thirdPageValidation.joinDate || null,
        status: candidate.status || '',
        nic: firstPageValidation.nic?.trim() || '',
        dob: firstPageValidation.dateOfBirth || null,
        genderCode:
          selectedGender === 'male'
            ? 'Male'
            : selectedGender === 'female'
            ? 'Female'
            : '',
        maritalStatusCode:
          selectedMaritalStatus === 'married'
            ? 'Married'
            : selectedMaritalStatus === 'single'
            ? 'Single'
            : '',
        email: firstPageValidation.personalEmail?.trim() || '',
        officialEmail: firstPageValidation.officialEmail?.trim() || '',
        phoneNumber: secondPageValidation.mobileNumber || '', // Keep with + format
        address: secondPageValidation.permanentAddress?.trim() || '',
        currentAddress: secondPageValidation.currentAddress?.trim() || '',
        emergencyContactDetails: [],
        employeeType:
          selectedType === 'intern'
            ? 'INTERN'
            : selectedType === 'probation'
            ? 'PROBATION'
            : 'FULLTIME',
        designationCode: selectedDesignation?.value || '',
        departmentCode:
          selectedDesignation?.department ||
          thirdPageValidation.department ||
          '',
        tier: selectedDesignation?.tier || thirdPageValidation.tier || '',
        primaryManager: selectedManager?.value || '',
        joinedDate: thirdPageValidation.joinDate || '',
        employeeTypeStartingDate:
          thirdPageValidation.fromDate || thirdPageValidation.joinDate || '',
        role:
          Array.isArray(selectedUserRole) && selectedUserRole.length > 0
            ? selectedUserRole.map((role) => role.label).join(',')
            : 'General User',

        createJiraAccount: true,
        jiraLead: false,
        employeeTypeEndingDate: null,
        carrierStartDate: thirdPageValidation.joinDate || ''
      };

      // Add emergency contact if provided - preserve + format
      if (
        secondPageValidation.emergencyContactName?.trim() &&
        secondPageValidation.emergencyContactNumber?.trim()
      ) {
        updatePayload.emergencyContactDetails.push({
          name: secondPageValidation.emergencyContactName.trim(),
          relationship:
            secondPageValidation.emergencyContactRelationship?.trim() ||
            'Emergency Contact',
          number: secondPageValidation.emergencyContactNumber || '' // Keep with + format
        });
      }

      // Map candidate data to AddEmployee interface
      const addEmployeePayload = {
        name: `${firstPageValidation.firstName?.trim() || ''} ${
          firstPageValidation.lastName?.trim() || ''
        }`.trim(),
        nic: firstPageValidation.nic?.trim() || '',
        gender:
          selectedGender === 'male'
            ? 'Male'
            : selectedGender === 'female'
            ? 'Female'
            : '',
        maritalStatus:
          selectedMaritalStatus === 'married'
            ? 'Married'
            : selectedMaritalStatus === 'single'
            ? 'Single'
            : '',
        dob: firstPageValidation.dateOfBirth || '',
        phoneNumber: secondPageValidation.mobileNumber || '', // Keep with + format
        emergencyContactName:
          secondPageValidation.emergencyContactName?.trim() || '',
        emergencyContactRelation:
          secondPageValidation.emergencyContactRelationship?.trim() || '',
        emergencyContactNumber: secondPageValidation.emergencyContactNumber || '', // Keep with + format
        address: secondPageValidation.permanentAddress?.trim() || '',
        currentAddress: secondPageValidation.currentAddress?.trim() || '',
        skypeID: 'test123', // If you have this field, map it
        email: firstPageValidation.officialEmail?.trim() || '',
        type: selectedType || '',
        employeeId: thirdPageValidation.employeeId?.trim() || '',
        employeeNumber: thirdPageValidation.employeeId?.trim() || '',
        designation: selectedDesignation?.label || '',
        tier: selectedDesignation?.tier || thirdPageValidation.tier || '',
        employeeType:
          selectedType === 'intern'
            ? 'INTERN'
            : selectedType === 'probation'
            ? 'PROBATION'
            : 'FULLTIME',
        joinedDate: thirdPageValidation.joinDate || '',
        EmployeeTypeStartingDate:
          thirdPageValidation.fromDate || thirdPageValidation.joinDate || '',
        EmployeeTypeEndingDate: null,
        file: undefined, // Map if available
        primaryManager:
          selectedManager?.id ||
          selectedManager?.employeeId ||
          selectedManager?.value ||
          '',
        createJiraAccount: true,
        role:
          Array.isArray(selectedUserRole) && selectedUserRole.length > 0
            ? selectedUserRole.map((role) => role.label).join(',')
            : 'General User',
        jiraLead: false,
        bankName: null, // Required by AddEmployee type
        accountNumber: null,
        accountName: null,
        branch: null,
        branchCode: null
      };

      updateCandidate(candidate.id, updatePayload)
        .then(() => {
          // 2. If update succeeds, add employee
          addEmployee(addEmployeePayload)
            .then(async (empResponse) => {
              const createdEmployeeId =
                addEmployeePayload.employeeId ||
                empResponse?.data?.employeeId ||
                empResponse?.data?.employeeNumber;
              const reporterId =
                selectedManager?.id ||
                selectedManager?.employeeId ||
                selectedManager?.value ||
                thirdPageValidation.reportingManagerId ||
                '';

              if (reporterId && createdEmployeeId) {
                try {
                  await addManagers([
                    {
                      EmployeeId: createdEmployeeId,
                      ReporterId: reporterId,
                      Level: 'primary'
                    }
                  ]);
                } catch (error) {
                  console.warn(
                    'Failed to save reporting manager (non-blocking):',
                    error
                  );
                }
              }

              // 3. If addEmployee succeeds, update EMS user created
              updateEmsUserCreated(candidate.id, true)
                .then(() => {
                  setIsLoading(false);
                  dispatch(AppActions.hideLoader());

                  if (onSuccess) onSuccess('EMS employee account has been created!');
                  resetFormAndClose();
                })
                .catch((error) => {
                  setIsLoading(false);
                  dispatch(AppActions.hideLoader());
                  let errorMsg = 'Unable to create EMS user. Please try again.';
                  if (error?.response?.data?.message) {
                    errorMsg = error.response.data.message;
                  } else if (error?.message) {
                    errorMsg = error.message;
                  }
                  showSnackbar(errorMsg, 'error');
                  if (onError)
                    onError(error.message || 'Failed to create EMS user');
                });

              ///This is a temporary fix until the employee service is fully migrated to Java.
              createDocumentAcknowledgement(empResponse.data.employeeId).catch(
                (error) => {
                  // Non-blocking step: do not show this as a user-facing error
                  // because EMS user creation can still complete successfully.
                  console.warn(
                    'Document acknowledgement failed (non-blocking):',
                    error
                  );
                }
              );
            })
            .catch((error) => {
              setIsLoading(false);
              dispatch(AppActions.hideLoader());
              let errorMsg = 'Unable to add employee. Please try again.';
              if (error?.response?.data?.message) {
                errorMsg = error.response.data.message;
              } else if (error?.message) {
                errorMsg = error.message;
              }
              showSnackbar(errorMsg, 'error');
              if (onError) onError(error.message || 'Failed to add employee');
            });
        })
        .catch((error) => {
          setIsLoading(false);
          dispatch(AppActions.hideLoader());
          let errorMsg = 'Unable to update candidate. Please try again.';
          if (error?.response?.data?.message) {
            errorMsg = error.response.data.message;
          } else if (error?.message) {
            errorMsg = error.message;
          }
          showSnackbar(errorMsg, 'error');
          if (onError) onError(error.message || 'Failed to update candidate');
        });
    }
  };

  const stackActionButtonsOnMobile = isCreateEmsUserMode;

  const isCreateCandidateSubmitDisabled =
    isCreateMode &&
    (!firstPageValidation?.firstName?.trim() ||
      !firstPageValidation?.lastName?.trim() ||
      !secondPageValidation?.mobileNumber?.trim() ||
      !thirdPageValidation?.joinDate?.trim() ||
      !selectedDesignation);

  const sharedActionButtonSx = {
    width: {
      xs: stackActionButtonsOnMobile
        ? '100%'
        : isViewMode
        ? '100%'
        : 'calc(50% - 4px)',
      sm: '176px'
    },
    minWidth: { xs: '130px', sm: '176px' },
    height: { xs: 36, sm: 38 },
    px: { xs: 1.5, sm: 2 },
    fontSize: { xs: '0.8rem', sm: '0.85rem' },
    lineHeight: 1.1,
    textTransform: 'none',
    whiteSpace: 'nowrap'
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        className="candidate-form"
        sx={{
          ...dialogStyles,
          '& .MuiDialog-paper': {
            ...dialogStyles['& .MuiDialog-paper'],
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: {
              xs: 'calc(100vw - 16px)',
              sm: 'calc(100vw - 48px)',
              md: '920px',
              lg: '1000px'
            },
            maxWidth: {
              xs: '100%',
              sm: '95%',
              md: '920px',
              lg: '1000px'
            },
            height: {
              xs: 'calc(100vh - 16px)',
              sm: '90vh'
            },
            maxHeight: {
              xs: 'calc(100vh - 16px)',
              sm: '90vh'
            }
          }
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff'
          }}
        >
          <FormHeader mode={mode} />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          <DialogContent
            dividers
            sx={{
              ...dialogContentStyles,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1.5, sm: 2, md: 2.5 }
            }}
          >
            {/* Basic Information Section */}
            <BasicInformation
              firstPageValidation={firstPageValidation}
              setFirstPageValidation={setFirstPageValidation}
              firstErrors={firstErrors}
              clearError={clearError}
              selectedGender={selectedGender}
              setSelectedGender={setSelectedGender}
              selectedMaritalStatus={selectedMaritalStatus}
              setSelectedMaritalStatus={setSelectedMaritalStatus}
              isViewMode={isViewMode}
              isCreateEmsUserMode={isCreateEmsUserMode}
              register={register}
            />

            <Divider sx={{ my: { xs: 1.5, sm: 2, md: 3 } }} />

            {/* Contact Details Section */}
            <ContactDetails
              firstPageValidation={firstPageValidation}
              setFirstPageValidation={setFirstPageValidation}
              firstErrors={firstErrors}
              clearError={clearError}
              secondPageValidation={secondPageValidation}
              setSecondPageValidation={setSecondPageValidation}
              secondErrors={secondErrors}
              isViewMode={isViewMode}
              isCreateEmsUserMode={isCreateEmsUserMode}
              isCreateMode={isCreateMode}
              register={register}
            />

            <Divider sx={{ my: { xs: 1.5, sm: 2, md: 3 } }} />

            {/* Employment Details Section */}
            <EmploymentDetails
              thirdPageValidation={thirdPageValidation}
              setThirdPageValidation={setThirdPageValidation}
              thirdErrors={thirdErrors}
              setThirdErrors={setThirdErrors}
              selectedDesignation={selectedDesignation}
              setSelectedDesignation={setSelectedDesignation}
              selectedManager={selectedManager}
              setSelectedManager={setSelectedManager}
              selectedUserRole={selectedUserRole}
              setSelectedUserRole={setSelectedUserRole}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              isViewMode={isViewMode}
              isEditMode={isEditMode}
              isCreateEmsUserMode={isCreateEmsUserMode}
              register={register}
            />
          </DialogContent>

          <DialogActions
            sx={{
              ...dialogActionsStyles,
              backgroundColor: '#fff',
              borderTop: '1px solid #eef2f7',
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1, sm: 1.5 },
              gap: 0.75,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              alignItems: { xs: 'stretch', sm: 'center' }
            }}
          >
            <Button
              onClick={handleClose}
              sx={{
                ...buttonStyles,
                ...sharedActionButtonSx,
                background: '#F5F7FF',
                color: '#000'
              }}
              disabled={isLoading || globalLoading}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>

            {!isViewMode && (
              <Button
                type="submit"
                sx={{
                  ...buttonStyles,
                  ...sharedActionButtonSx,
                  background: isLoading || globalLoading ? '#ccc' : '#0069E4',
                  color: 'white'
                }}
                variant="contained"
                disabled={
                  isLoading ||
                  globalLoading ||
                  isCreateCandidateSubmitDisabled
                }
                title={
                  isLoading || globalLoading
                    ? 'Processing your request...'
                    : isCreateCandidateSubmitDisabled
                    ? 'Fill all required fields to enable Save'
                    : 'Click to save information'
                }
              >
                {isLoading || globalLoading ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: 'white' }}
                    />
                    {isCreateEmsUserMode
                      ? 'Creating...'
                      : isCreateMode
                      ? 'Creating...'
                      : 'Updating...'}
                  </>
                ) : isCreateEmsUserMode ? (
                  'Create EMS User'
                ) : isCreateMode ? (
                  'Save'
                ) : (
                  'Update'
                )}
              </Button>
            )}
          </DialogActions>

          <SnackBar
            snackBarOpen={snackBarOpen}
            setSnackBarOpen={setSnackBarOpen}
            snackBarMessage={snackBarMessage}
            snackbarSeverity={snackbarSeverity}
          />
        </Box>
      </Dialog>

      {/* Discard Confirmation Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={handleDiscardCancel}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
            pb: 1
          }}
        >
          Discard Changes?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#666', fontSize: '14px' }}>
            {getDiscardMessage()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleDiscardCancel}
            sx={{
              background: '#F5F7FF',
              color: '#000',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                background: '#E8ECFF'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDiscardConfirm}
            variant="contained"
            sx={{
              background: '#DC3545',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                background: '#C82333'
              }
            }}
          >
            Yes, Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CandidateForm;